import { useEffect, useState } from "react";
import Papa from "papaparse";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { SideNav } from "@/components/SideNav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UserPlus, Pencil, Trash2, Users, Upload, Download, CheckCircle2, XCircle, Receipt, MessageCircle, Key, LogOut } from "lucide-react";
import { PayslipDialog } from "@/components/PayslipDialog";
import { WhatsAppLinkDialog } from "@/components/WhatsAppLinkDialog";

// PROBLEM C + D fix (2026-02-29 second re-audit):
//   * ROLES is now the FILTER-TAB list only. It includes SuperAdmin so that the tabs still
//     add up to All. Adding it here does NOT change what an Admin can create/edit.
//   * ASSIGNABLE_ROLES is the Add/Edit role dropdown list. SuperAdmin is intentionally EXCLUDED
//     to prevent an Admin from promoting anyone to SuperAdmin via the UI. When editing an
//     existing SuperAdmin (see `roleOptionsForForm` below) we transparently re-include their
//     current role in the dropdown so the form does not silently downgrade them on save.
const ROLES = ["SuperAdmin", "Admin", "IT", "Project Lead", "Supervisor", "Promoter", "Coca-Cola"];
const ASSIGNABLE_ROLES = ["Admin", "IT", "Project Lead", "Supervisor", "Promoter", "Coca-Cola"];

// PROBLEM C fix (2026-02-29 second re-audit) — canonicalise the legacy DB string "Client"
// to its display peer "Coca-Cola" on BOTH sides of the equality so users still carrying the
// old raw role match the "Coca-Cola" tab. displayRole() remains reserved for visible labels
// only. Do NOT rewrite the underlying role in the DB — this normalisation is view-only.
const canonRole = (r) => (r === "Client" ? "Coca-Cola" : r);
const matchesRoleFilter = (userRole, tabRole) => canonRole(userRole) === canonRole(tabRole);

// PROBLEM D fix (2026-02-29 second re-audit) — the Add/Edit role dropdown normally renders
// ASSIGNABLE_ROLES (SuperAdmin excluded). But when we are EDITING a user whose current role
// is not in that list (e.g. an existing SuperAdmin), we transparently re-inject their own
// current role at the top of the options so the <Select value=...> still finds a matching
// item and their role is preserved verbatim on save. This prevents a silent downgrade
// without opening a privilege-escalation path for Admins creating new users.
const roleOptionsForForm = (currentRole) => (
  currentRole && !ASSIGNABLE_ROLES.includes(currentRole)
    ? [currentRole, ...ASSIGNABLE_ROLES]
    : ASSIGNABLE_ROLES
);

// Legacy "Client" role in the DB is displayed as "Coca-Cola" everywhere.
// ITEM D.15-16 (2026-02-29) — display renames. Internal role strings stay the same.
const displayRole = (r) => {
  if (r === "Client") return "Coca-Cola";
  if (r === "Supervisor") return "Co-ordinator";
  if (r === "Project Lead") return "Manager";
  return r;
};
// PROBLEM B fix (2026-02-29 audit) — compare raw role to raw role. displayRole() is used
// ONLY for the visible tab label, never for equality tests. Previously "Co-ordinator" and
// "Manager" never matched their underlying "Supervisor"/"Project Lead" DB strings so both
// tabs read (0) regardless of user count.

const empty = () => ({
  id: null, name: "", phone: "", login_id: "", email: "", role: "Promoter",
  assigned_store: "", assigned_stores_list: [], monthly_salary: 0, password: "",
});

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [testPhones, setTestPhones] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("All");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState([]); // parsed rows for preview
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [payslipUser, setPayslipUser] = useState(null);
  const [waUser, setWaUser] = useState(null);
  const [pwShown, setPwShown] = useState(null); // { name, phone, password }

  const load = async () => {
    setLoading(true);
    try {
      const [u, s, c] = await Promise.all([
        api.get("/users"), api.get("/stores"), api.get("/config"),
      ]);
      setUsers(u.data); setStores(s.data);
      setTestPhones(new Set(c.data.test_phones || []));
    } catch { toast.error("Failed to load users"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(empty()); setOpen(true); };
  const openEdit = (u) => {
    setForm({
      id: u.id, name: u.name || "", phone: u.phone || "", login_id: u.login_id || "", email: u.email || "", role: u.role || "Promoter",
      assigned_store: u.assigned_store || "",
      assigned_stores_list: u.assigned_stores_list || [],
      monthly_salary: u.monthly_salary || 0,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    if (!/^\d{10}$/.test(form.phone)) return toast.error("Phone must be 10 digits");
    if (form.role === "Promoter" && !form.assigned_store) return toast.error("Assign a store to the promoter");
    if (!form.id && form.password && form.password.length < 6) return toast.error("Password must be at least 6 characters");
    // Optional login_id — uniqueness check happens server-side (returns 400 with clear detail)
    const lid = (form.login_id || "").trim();
    if (lid && (lid.length < 4 || lid.length > 32)) return toast.error("Login ID must be 4-32 characters");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(), phone: form.phone,
        login_id: lid || null,   // blank => backend auto-generates COKE9-XXXX
        email: form.email || null, role: form.role,
        assigned_store: form.role === "Promoter" ? form.assigned_store : null,
        // PROBLEM A fix (2026-02-29 audit) — Supervisor + Project Lead + IT all keep their
        // assigned_stores_list on save. Previously the ternary discarded the selection for
        // Project Lead and IT, silently sending [] to the API and showing "0 stores" in Scope.
        assigned_stores_list: ["Supervisor", "Project Lead", "IT"].includes(form.role) ? form.assigned_stores_list : [],
        monthly_salary: form.role === "Promoter" ? Number(form.monthly_salary) || 0 : null,
      };
      if (form.id) {
        await api.patch(`/users/${form.id}`, payload);
        toast.success("User updated");
        setOpen(false); load();
      } else {
        payload.password = (form.password || "").trim() || null;
        const { data } = await api.post("/users", payload);
        toast.success("User added");
        setOpen(false);
        setPwShown({ name: data.name, phone: data.phone, login_id: data.login_id, password: data.initial_password });
        load();
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "Failed to save";
      toast.error(detail);
    } finally { setSaving(false); }
  };

  const resetPw = async (user) => {
    if (!window.confirm(`Reset password for ${user.name}? Their current sessions will be logged out.`)) return;
    try {
      const { data } = await api.post(`/users/${user.id}/reset-password`);
      setPwShown({ name: data.name, phone: data.phone, password: data.new_password });
      toast.success("Password reset");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const del = async (hard = false) => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/users/${confirmDelete.id}${hard ? "?hard=true" : ""}`);
      toast.success(hard ? "User permanently deleted" : "User deactivated");
      setConfirmDelete(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete");
    }
  };

  const filtered = tab === "All" ? users : users.filter(u => matchesRoleFilter(u.role, tab));
  const storeOptions = stores;
  const toggleSupervisorStore = (id) => {
    setForm((f) => ({
      ...f,
      assigned_stores_list: f.assigned_stores_list.includes(id)
        ? f.assigned_stores_list.filter(x => x !== id)
        : [...f.assigned_stores_list, id],
    }));
  };

  // ----- CSV bulk import -----
  const downloadSample30 = async () => {
    try {
      const res = await api.get("/samples/promoters.csv", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "promoters-sample-30.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download sample");
    }
  };

  const downloadTemplate = () => {
    const csv = [
      "name,phone,role,store_code,store_codes,email,monthly_salary",
      "Priya Sharma,9111111111,Promoter,REL-1002,,priya@example.in,15000",
      "Ravi Kumar,9111111112,Promoter,REL-1003,,,",
      "Sunita Yadav,9222222221,Supervisor,,REL-1001|REL-1002|REL-1003,sunita@example.in,",
      "CCI Rep 2,9333333331,Client,,,cci2@example.in,",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "coke-fountain-users-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const handleCsvFile = (file) => {
    if (!file) return;
    setImportResult(null);
    Papa.parse(file, {
      header: true, skipEmptyLines: true,
      complete: (res) => {
        const rows = (res.data || []).map((r) => ({
          name: (r.name || "").trim(),
          phone: (r.phone || "").trim(),
          role: (r.role || "").trim(),
          store_code: (r.store_code || "").trim(),
          store_codes: (r.store_codes || "").trim(),
          email: (r.email || "").trim(),
          monthly_salary: r.monthly_salary ? Number(String(r.monthly_salary).replace(/[^\d.]/g, "")) : null,
        })).filter(r => r.name || r.phone || r.role);
        setImportRows(rows);
        if (rows.length === 0) toast.error("No valid rows found in CSV");
      },
      error: (err) => toast.error("Parse error: " + err.message),
    });
  };

  const submitImport = async () => {
    if (!importRows.length) return;
    setImporting(true);
    try {
      const { data } = await api.post("/users/bulk-import", { rows: importRows });
      setImportResult(data);
      toast.success(`Imported ${data.created}, ${data.errors.length} errors`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally { setImporting(false); }
  };

  const closeImport = () => {
    setImportOpen(false);
    setImportRows([]);
    setImportResult(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader title="Manage Users" subtitle="Add / edit / remove Admin, IT, Manager, Co-ordinators, Promoters and Coca-Cola users" />
      <div className="flex-1 flex">
        <SideNav />
        <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 py-6 space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList>
                <TabsTrigger data-testid="user-tab-all" value="All">All ({users.length})</TabsTrigger>
                {ROLES.map(r => (
                  <TabsTrigger key={r} data-testid={`user-tab-${r.toLowerCase().replace(/\s+/g, "-")}`} value={r}>
                    {displayRole(r)} ({users.filter(u => matchesRoleFilter(u.role, r)).length})
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            <div className="flex items-center gap-2">
              <Button data-testid="import-csv-btn" onClick={() => setImportOpen(true)} variant="outline">
                <Upload className="h-4 w-4 mr-2" /> Import CSV
              </Button>
              <Button data-testid="add-user-btn" onClick={openNew} className="bg-brand-red hover:bg-[#B31721]">
                <UserPlus className="h-4 w-4 mr-2" /> Add User
              </Button>
            </div>
          </div>

          <Card className="rounded-xl border-gray-200 bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="text-left px-4 py-3">Name</th>
                    <th className="text-left px-4 py-3">Role</th>
                    <th className="text-left px-4 py-3">Phone</th>
                    <th className="text-left px-4 py-3">Login ID</th>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Scope</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={7} className="text-center py-8 text-gray-500">Loading…</td></tr>}
                  {!loading && filtered.map(u => {
                    const isTest = testPhones.has(u.phone);
                    const scope = u.role === "Promoter"
                      ? (stores.find(s => s.id === u.assigned_store)?.store_name || "—")
                      : (u.role === "Supervisor" || u.role === "IT" || u.role === "Project Lead")
                        ? `${(u.assigned_stores_list || []).length} stores`
                        : "—";
                    return (
                      <tr key={u.id} className="border-t border-gray-100" data-testid={`user-row-${u.phone}`}>
                        <td className="px-4 py-3">
                          <div className="font-semibold flex items-center gap-2">
                            <span>{u.name}</span>
                            {isTest && <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50 text-[10px]">Test</Badge>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {(() => {
                            const dr = displayRole(u.role);
                            const cls =
                              dr === "Admin" ? "bg-neutral-900 text-white hover:bg-neutral-900" :
                              dr === "Coordinator" ? "bg-blue-100 text-blue-800 hover:bg-blue-100" :
                              dr === "Promoter" ? "bg-red-100 text-red-800 hover:bg-red-100" :
                              dr === "IT" ? "bg-purple-100 text-purple-800 hover:bg-purple-100" :
                              dr === "Manager" ? "bg-indigo-100 text-indigo-800 hover:bg-indigo-100" :
                              dr === "Coca-Cola" ? "bg-green-100 text-green-800 hover:bg-green-100" :
                              "bg-gray-100 text-gray-800 hover:bg-gray-100";
                            return <Badge className={cls} data-testid={`user-role-${u.phone}`}>{dr || "—"}</Badge>;
                          })()}
                        </td>
                        <td className="px-4 py-3 text-gray-700">+91 {u.phone}</td>
                        <td className="px-4 py-3 text-gray-700 text-xs font-mono" data-testid={`user-login-id-${u.phone}`}>{u.login_id || "—"}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{u.email || "—"}</td>
                        <td className="px-4 py-3 text-gray-700 text-xs">{scope}</td>
                        <td className="px-4 py-3 text-right">
                          {u.role === "Promoter" && (
                            <Button data-testid={`payslip-user-${u.phone}`} size="sm" variant="outline" onClick={() => setPayslipUser(u)} className="mr-1">
                              <Receipt className="h-3.5 w-3.5 mr-1" /> Payslip
                            </Button>
                          )}
                          {(u.role === "Promoter" || u.role === "Supervisor") && !isTest && (
                            <Button data-testid={`whatsapp-user-${u.phone}`} size="sm" variant="outline" onClick={() => setWaUser(u)} className="mr-1 text-green-700 border-green-200 hover:bg-green-50">
                              <MessageCircle className="h-3.5 w-3.5 mr-1" /> WhatsApp
                            </Button>
                          )}
                          <Button data-testid={`reset-pw-${u.phone}`} size="sm" variant="outline"
                            onClick={() => resetPw(u)}
                            className="mr-1 text-amber-700 border-amber-200 hover:bg-amber-50">
                            <Key className="h-3.5 w-3.5 mr-1" /> Reset PW
                          </Button>
                          <Button data-testid={`edit-user-${u.phone}`} size="sm" variant="outline" onClick={() => openEdit(u)} className="mr-1">
                            <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                          </Button>
                          <Button
                            data-testid={`delete-user-${u.phone}`} size="sm" variant="outline"
                            onClick={() => setConfirmDelete(u)}
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                  {!loading && filtered.length === 0 && (
                    <tr><td colSpan={6} className="text-center py-8 text-gray-500">No users in this role yet</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="text-xs text-gray-500 flex items-start gap-2">
            <Users className="h-3.5 w-3.5 mt-0.5" />
            Test accounts (marked with the amber "Test" tag) can now be deleted like any other user. Real Co-ordinators, Promoters, and Coca-Cola contacts should be added here with their actual names & phone numbers.
          </div>
        </main>
      </div>

      {/* Add / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="user-form-dialog" className="max-w-lg rounded-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{form.id ? "Edit user" : "Add new user"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Role</label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, assigned_store: "", assigned_stores_list: [] })}>
                <SelectTrigger data-testid="user-role-select" className="h-12"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roleOptionsForForm(form.role).map(r => <SelectItem key={r} value={r}>{displayRole(r)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Full name</label>
              <Input data-testid="user-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Priya Sharma" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Phone (10-digit)</label>
                <div className="flex items-center gap-2">
                  <div className="h-10 px-2 flex items-center rounded-md border border-gray-200 bg-gray-50 text-gray-500 text-sm">+91</div>
                  <Input data-testid="user-phone-input" inputMode="numeric" maxLength={10}
                    value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "") })} />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Login ID (optional)</label>
                <Input data-testid="user-login-id-input"
                  placeholder="e.g. COKE9-0007 or leave blank"
                  value={form.login_id}
                  onChange={(e) => setForm({ ...form, login_id: e.target.value.trim().toUpperCase() })} />
                <div className="text-[10px] text-gray-400 mt-0.5">Blank = auto-generate COKE9-XXXX. Must be unique.</div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Email (optional)</label>
                <Input data-testid="user-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            </div>

            {form.role === "Promoter" && (
              <>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Assigned store</label>
                  <Select value={form.assigned_store} onValueChange={(v) => setForm({ ...form, assigned_store: v })}>
                    <SelectTrigger data-testid="user-store-select" className="h-12"><SelectValue placeholder="Select store" /></SelectTrigger>
                    <SelectContent className="max-h-64">
                      {storeOptions.map(s => <SelectItem key={s.id} value={s.id}>{s.store_name} · {s.city}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Monthly salary ₹ (optional)</label>
                  <Input data-testid="user-salary-input" inputMode="numeric" value={form.monthly_salary}
                    onChange={(e) => setForm({ ...form, monthly_salary: Number(e.target.value.replace(/\D/g, "")) || 0 })} />
                </div>
              </>
            )}

            {(form.role === "Supervisor" || form.role === "IT" || form.role === "Project Lead") && (
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">
                  Assigned stores ({form.assigned_stores_list.length} selected)
                </label>
                <div className="mt-2 max-h-56 overflow-y-auto rounded-lg border border-gray-200 divide-y divide-gray-100">
                  {storeOptions.map(s => {
                    const checked = form.assigned_stores_list.includes(s.id);
                    return (
                      <label key={s.id} data-testid={`sup-store-${s.store_no}`}
                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 ${checked ? "bg-red-50" : ""}`}>
                        <input type="checkbox" checked={checked} onChange={() => toggleSupervisorStore(s.id)} className="accent-brand-red" />
                        <span className="text-sm">{s.store_name}</span>
                        <span className="text-xs text-gray-500 ml-auto">{s.city}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            {!form.id && (
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">
                  Initial password (leave empty to auto-generate)
                </label>
                <Input data-testid="user-password-input" value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Min 6 characters — will be shown once for you to share" />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-user-btn" onClick={save} disabled={saving} className="bg-brand-red hover:bg-[#B31721]">
              {form.id ? "Save changes" : "Create user"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payslip + WhatsApp side dialogs */}
      <PayslipDialog user={payslipUser} open={!!payslipUser} onOpenChange={(v) => !v && setPayslipUser(null)} />
      <WhatsAppLinkDialog user={waUser} open={!!waUser} onOpenChange={(v) => !v && setWaUser(null)} />

      {/* Password display dialog — shown once after create/reset */}
      <Dialog open={!!pwShown} onOpenChange={(v) => !v && setPwShown(null)}>
        <DialogContent data-testid="password-display-dialog" className="rounded-2xl max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Key className="h-5 w-5 text-brand-red" /> Password ready</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="text-gray-700">Share these credentials with <span className="font-semibold">{pwShown?.name}</span> (+91 {pwShown?.phone}) via call or WhatsApp. The password will NOT be shown again.</div>
            {pwShown?.login_id && (
              <div className="rounded-lg bg-gray-50 border border-gray-200 px-3 py-2 flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Login ID</div>
                  <div className="font-mono text-sm font-semibold" data-testid="new-user-login-id">{pwShown.login_id}</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(pwShown.login_id); toast.success("Copied"); }} data-testid="copy-login-id-btn">Copy</Button>
              </div>
            )}
            <div className="rounded-xl bg-neutral-900 text-white p-4 flex items-center justify-between">
              <span className="font-mono text-2xl tracking-widest" data-testid="password-display-value">{pwShown?.password}</span>
              <Button size="sm" variant="outline" className="bg-white text-black"
                onClick={() => { navigator.clipboard.writeText(pwShown?.password || ""); toast.success("Copied"); }}
                data-testid="copy-password-btn">Copy</Button>
            </div>
            <Button
              data-testid="whatsapp-password-btn"
              onClick={() => {
                const msg = `Hi ${pwShown?.name || ""}, your Coke @9 login is ready.%0A%0APhone: ${pwShown?.phone}%0APassword: ${pwShown?.password}%0A%0AAfter first login, please ask your Admin to reset the password so only you know it.`;
                window.open(`https://wa.me/91${pwShown?.phone}?text=${msg}`, "_blank", "noopener");
              }}
              className="w-full bg-[#25D366] hover:bg-[#1EBE59] text-white font-semibold">
              <MessageCircle className="h-4 w-4 mr-2" /> Send via WhatsApp
            </Button>
            <div className="text-xs text-gray-500">Any active session for this user has been logged out.</div>
          </div>
          <DialogFooter>
            <Button data-testid="password-display-close-btn" onClick={() => setPwShown(null)} className="bg-brand-red hover:bg-[#B31721]">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CSV Import dialog */}
      <Dialog open={importOpen} onOpenChange={(v) => v ? setImportOpen(true) : closeImport()}>
        <DialogContent data-testid="import-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-display">Bulk import users from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="rounded-xl bg-gray-50 border border-gray-200 p-4 text-sm">
              <div className="font-semibold mb-2">CSV columns (header row required):</div>
              <code className="text-xs text-gray-700 block break-all">
                name, phone, role, store_code, store_codes, email, monthly_salary
              </code>
              <ul className="mt-2 text-xs text-gray-600 space-y-1 list-disc list-inside">
                <li><b>role</b> must be one of: Admin, Supervisor, Promoter, Client</li>
                <li><b>store_code</b> required for Promoter (e.g. <code>REL-1002</code>)</li>
                <li><b>store_codes</b> for Co-ordinator — pipe-separated, e.g. <code>REL-1001|REL-1002|REL-1003</code></li>
                <li>Duplicate phones and unknown stores will be skipped and reported</li>
              </ul>
              <Button data-testid="download-template-btn" size="sm" variant="outline" onClick={downloadTemplate} className="mt-3 mr-2">
                <Download className="h-3.5 w-3.5 mr-1" /> Empty template
              </Button>
              <Button data-testid="download-sample30-btn" size="sm" variant="outline" onClick={downloadSample30} className="mt-3">
                <Download className="h-3.5 w-3.5 mr-1" /> Sample 30 promoters
              </Button>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Choose CSV file</label>
              <Input
                data-testid="csv-file-input"
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => handleCsvFile(e.target.files?.[0])}
                className="mt-1 file:mr-3 file:rounded-md file:border-0 file:bg-red-50 file:text-brand-red file:px-3 file:py-2 file:font-semibold"
              />
            </div>

            {importRows.length > 0 && !importResult && (
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500 mb-2">
                  Preview ({importRows.length} rows)
                </div>
                <div className="rounded-lg border border-gray-200 overflow-hidden max-h-72 overflow-y-auto">
                  <table className="w-full text-xs" data-testid="import-preview-table">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left p-2">#</th>
                        <th className="text-left p-2">Name</th>
                        <th className="text-left p-2">Phone</th>
                        <th className="text-left p-2">Role</th>
                        <th className="text-left p-2">Store(s)</th>
                        <th className="text-left p-2">Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importRows.map((r, i) => (
                        <tr key={`${r.phone || r.name || "row"}-${i}`} className="border-t border-gray-100">
                          <td className="p-2 text-gray-500">{i + 1}</td>
                          <td className="p-2 font-medium">{r.name}</td>
                          <td className="p-2">{r.phone}</td>
                          <td className="p-2"><Badge variant="outline">{r.role || "—"}</Badge></td>
                          <td className="p-2 text-gray-600">{r.store_code || r.store_codes || "—"}</td>
                          <td className="p-2 text-gray-500">{r.email || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {importResult && (
              <div className="space-y-3" data-testid="import-result">
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg bg-green-50 border border-green-200 p-3">
                    <div className="flex items-center gap-2 text-green-800 font-semibold">
                      <CheckCircle2 className="h-4 w-4" /> Created
                    </div>
                    <div className="font-display text-2xl font-black text-green-900">{importResult.created}</div>
                  </div>
                  <div className="rounded-lg bg-red-50 border border-red-200 p-3">
                    <div className="flex items-center gap-2 text-red-800 font-semibold">
                      <XCircle className="h-4 w-4" /> Errors
                    </div>
                    <div className="font-display text-2xl font-black text-red-900">{importResult.errors.length}</div>
                  </div>
                </div>

                {importResult.errors.length > 0 && (
                  <div>
                    <div className="text-xs font-bold uppercase tracking-[0.1em] text-red-800 mb-2">Errors</div>
                    <div className="rounded-lg border border-red-200 bg-red-50/40 overflow-hidden max-h-56 overflow-y-auto">
                      <table className="w-full text-xs" data-testid="import-errors-table">
                        <thead className="bg-red-50 sticky top-0">
                          <tr><th className="text-left p-2">Row</th><th className="text-left p-2">Name</th><th className="text-left p-2">Phone</th><th className="text-left p-2">Error</th></tr>
                        </thead>
                        <tbody>
                          {importResult.errors.map((e, i) => (
                            <tr key={`${e.row}-${e.phone || i}`} className="border-t border-red-100">
                              <td className="p-2 text-red-700 font-medium">{e.row}</td>
                              <td className="p-2">{e.name || "—"}</td>
                              <td className="p-2">{e.phone || "—"}</td>
                              <td className="p-2 text-red-700">{e.error}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeImport}>
              {importResult ? "Close" : "Cancel"}
            </Button>
            {!importResult && (
              <Button
                data-testid="confirm-import-btn"
                onClick={submitImport}
                disabled={!importRows.length || importing}
                className="bg-brand-red hover:bg-[#B31721]"
              >
                <Upload className="h-4 w-4 mr-2" />
                {importing ? "Importing…" : `Import ${importRows.length} rows`}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <DialogContent data-testid="user-delete-dialog" className="rounded-2xl">
          <DialogHeader><DialogTitle>Delete user?</DialogTitle></DialogHeader>
          <div className="text-sm text-gray-700 space-y-2">
            <div>
              Choose how to delete <span className="font-semibold">{confirmDelete?.name}</span> ({confirmDelete?.phone}).
            </div>
            <div className="rounded-lg bg-gray-50 border border-gray-200 p-3 space-y-1 text-xs">
              <div><span className="font-bold text-gray-800">Deactivate</span> — user cannot log in anymore, historical records remain, can be restored later.</div>
              <div><span className="font-bold text-red-700">Delete permanently</span> — user row is wiped from the database (historical records they entered stay intact). Cannot be undone.</div>
            </div>
          </div>
          <DialogFooter className="gap-2 flex-wrap">
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button data-testid="confirm-soft-delete-user-btn" onClick={() => del(false)} variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50">
              Deactivate
            </Button>
            <Button data-testid="confirm-hard-delete-user-btn" onClick={() => del(true)} className="bg-red-600 hover:bg-red-700 text-white">
              <Trash2 className="h-4 w-4 mr-2" /> Delete permanently
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
